import React, { useContext } from "react";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";

import { Layout } from "../interfaces/layout";
import {
    SlideContainer,
    Subtitle,
    SlideTitle,
} from "../components/slideComponents";
import { RenderContentBlock } from "../components/RenderContentBlock";
import MicrositeContext from "../MicrositeContext";
import { getCompletedTransitionNames } from "../micrositeFunctions";
import { Content } from "./ContentImageContent";
import { Caption } from "../components/videoComponents";

const StyledSlideTitle = styled(SlideTitle)`
    flex: 1;
`;

const SlideBody = styled.div`
    display: flex;
    flex: 5;
    flex-direction: row;
    align-items: flex-start;
    padding-top: 1em;
    margin-bottom: 1rem;
    font-size: 0.7em;
`;

const CenterColumn = styled(animated.div)`
    height: 100%;
    width: 50%;
    display: flex;
    justify-content: center;
    padding: 0 1rem;
    flex-direction: column;
`;

const SlideColumnSection = styled(animated.div)`
    position: relative;
    z-index: 1;
`;

const SlideColumn = styled.div`
    width: 25%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    height: 100%;
`;

const Image = styled.img`
    width: 100%;
`;

const ImageFourCorners = ({
    settings,
    content,
    transitions,
    disableSequencing,
    pageName,
    groupName,
}: Layout) => {
    let {
        navigationState: { currentTransitionIndex },
        editing,
        slideSize,
        globalSettings,
        globalTheming,
        currentPageConfig,
    } = useContext(MicrositeContext);
    if (editing || disableSequencing) {
        currentTransitionIndex = 8;
    }
    const transitionsComplete = getCompletedTransitionNames(
        transitions,
        currentTransitionIndex
    );

    const highlightColor = settings.highlightColor
        ? settings.highlightColor
        : globalTheming?.highlightColor;

    return (
        <SlideContainer
            backgroundImage={globalSettings?.backgroundImage}
            settings={settings}
            slideWidth={slideSize?.width}
            slideHeight={slideSize?.height}
            fontSize={slideSize?.fontSize}
        >
            {!currentPageConfig?.hide_page_name && (
                <Subtitle highlightColor={highlightColor} groupName={groupName}>
                    {pageName}
                </Subtitle>
            )}
            <StyledSlideTitle
                size={settings.titleSize}
                hidden={!content.title || !content.title.value}
                style={useSpring({
                    opacity: transitionsComplete.includes("title") ? 1 : 0,
                })}
            >
                <RenderContentBlock {...content.title} />
            </StyledSlideTitle>
            <SlideBody>
                <SlideColumn>
                    <SlideColumnSection
                        style={useSpring({
                            opacity: transitionsComplete.includes(
                                "topLeftImage"
                            )
                                ? 1
                                : 0,
                        })}
                    >
                        {content.topLeftImage && content.topLeftImage.value && (
                            <Image
                                src={content.topLeftImage.value}
                                alt="Top left image"
                            />
                        )}
                    </SlideColumnSection>
                    <SlideColumnSection
                        style={useSpring({
                            opacity: transitionsComplete.includes(
                                "bottomLeftImage"
                            )
                                ? 1
                                : 0,
                        })}
                    >
                        {content.bottomLeftImage &&
                            content.bottomLeftImage.value && (
                                <Image
                                    src={content.bottomLeftImage.value}
                                    alt="Bottom left image"
                                />
                            )}
                    </SlideColumnSection>
                </SlideColumn>
                <CenterColumn
                    style={useSpring({
                        opacity: transitionsComplete.includes("content")
                            ? 1
                            : 0,
                    })}
                >
                    {!!content.content && Array.isArray(content.content.value) && (
                        <>
                            {content.content.value.map(
                                (column: Content, index: number) => {
                                    return (
                                        <Caption
                                            highlightColor={highlightColor}
                                            key={index}
                                            showing={true}
                                            bulletPoint={!!column.bullet}
                                        >
                                            <RenderContentBlock
                                                type="text"
                                                value={column.value}
                                                footnotes={
                                                    column.footnotes || []
                                                }
                                            />
                                        </Caption>
                                    );
                                }
                            )}
                        </>
                    )}
                </CenterColumn>
                <SlideColumn>
                    <SlideColumnSection
                        style={useSpring({
                            opacity: transitionsComplete.includes(
                                "topRightImage"
                            )
                                ? 1
                                : 0,
                        })}
                    >
                        {content.topRightImage &&
                            content.topRightImage.value && (
                                <Image
                                    src={content.topRightImage.value}
                                    alt="Top Right image"
                                />
                            )}
                    </SlideColumnSection>
                    <SlideColumnSection
                        style={useSpring({
                            opacity: transitionsComplete.includes(
                                "bottomRightImage"
                            )
                                ? 1
                                : 0,
                        })}
                    >
                        {content.bottomRightImage &&
                            content.bottomRightImage.value && (
                                <Image
                                    src={content.bottomRightImage.value}
                                    alt="Bottom right image"
                                />
                            )}
                    </SlideColumnSection>
                </SlideColumn>
            </SlideBody>
        </SlideContainer>
    );
};

export default ImageFourCorners;
